import { lazy, Suspense } from "react";
import ApexHeader from "@/components/apex/ApexHeader";
import ApexHero from "@/components/apex/ApexHero";
import SEOHead from "@/components/seo/SEOHead";

// Below-the-fold: lazy-load to shrink initial JS bundle and speed up LCP.
const ApexProducts = lazy(() => import("@/components/apex/ApexProducts"));
const ApexProcess = lazy(() => import("@/components/apex/ApexProcess"));
const ApexAbout = lazy(() => import("@/components/apex/ApexAbout"));
const ApexTeam = lazy(() => import("@/components/apex/ApexTeam"));
const ApexFooter = lazy(() => import("@/components/apex/ApexFooter"));

const SectionFallback = () => <div className="min-h-[40vh]" aria-hidden="true" />;

const DOMAIN = "https://tempstoragexpress.com";

const Index = () => {
  const homeJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "TSX - Temporary Storage Express",
      alternateName: "TSX",
      url: DOMAIN,
      logo: `${DOMAIN}/images/og-default.jpg`,
      description:
        "Engineered temporary grain storage systems: steel bunker walls, aeration, safety equipment, and complete bunker solutions.",
      address: {
        "@type": "PostalAddress",
        streetAddress: "2200 Gannett Avenue",
        addressLocality: "Des Moines",
        addressRegion: "IA",
        postalCode: "50321",
        addressCountry: "US",
      },
      telephone: "+1-515-380-9000",
      email: "sales@apexindustrialco.com",
      foundingDate: "2019",
      parentOrganization: { "@type": "Organization", name: "Apex Industrial" },
      sameAs: [
        "https://www.facebook.com/ApexIndustrial1/",
        "https://x.com/IndustrialAPEX",
        "https://www.linkedin.com/company/apex-industrial",
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "TSX",
      url: DOMAIN,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is a temporary grain storage bunker?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "A temporary grain bunker is a flat-bottomed, walled storage structure—typically steel walls anchored to a concrete or compacted pad and covered with a tarp. It provides large-volume grain storage that can be set up before harvest and dismantled or kept in place for future seasons.",
          },
        },
        {
          "@type": "Question",
          name: "How much grain can a TSX bunker hold?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Capacity depends on wall height, footprint, and pile angle. Systems range from a few hundred thousand bushels to several million bushels. Our team can size a system from a target bushel volume or available pad dimensions.",
          },
        },
        {
          "@type": "Question",
          name: "Are TSX bunker walls reusable?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. The steel wall panels are designed for repeated seasonal use. Wear items such as tarps, ground stakes, and flashing may need periodic replacement.",
          },
        },
        {
          "@type": "Question",
          name: "How long does it take to install a bunker?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Installation time depends on size and crew, but most systems are designed to be erected quickly with standard equipment so they can be ready before grain arrives.",
          },
        },
        {
          "@type": "Question",
          name: "How do I get a quote for temporary grain storage?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Visit https://tempstoragexpress.com/sign-up or call 515-380-9000. We typically need: target bushel capacity, available pad size, location, and timeline.",
          },
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="TSX | Temporary Grain Storage Systems & Bunkers"
        description="Engineered temporary grain storage by TSX: steel bunker walls, aeration, safety, and parts. Maximize bushel capacity and ship from Des Moines, Iowa."
        canonicalPath="/"
        jsonLd={homeJsonLd}
      />
      <ApexHeader />
      <main>
        <ApexHero />
        <Suspense fallback={<SectionFallback />}>
          <ApexProducts />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <ApexProcess />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <ApexAbout />
        </Suspense>
        <Suspense fallback={<SectionFallback />}>
          <ApexTeam />
        </Suspense>
      </main>
      <Suspense fallback={<SectionFallback />}>
        <ApexFooter />
      </Suspense>
    </div>
  );
};

export default Index;
